import React, { useState, useMemo } from "react";
import axios from "axios";
import { Permission, InputShareFieldProps, ResourceType } from "./entities";
import { createLogger } from "debug/DebugingLogger";
import { UserModelRole } from "target_wasm";
import {
  SelectDropdown,
  SelectOption,
} from "components/main/utils/SelectDropdown";
import { Button } from "components/common/Button";
import { EmailChipInput, EmailChip } from "./EmailChipInput";
import { useShareScope } from "./useShareScope";

const log = createLogger("ModelServices");

const CAPACITY_FALLBACK =
  "We've hit our account limit, so we couldn't invite everyone yet.";
const SHARE_FALLBACK = "Failed to share. Please try again.";

/// Refusals from the share doors carry their own wording, because only the
/// server knows how much it may say: a company org's table names the addresses
/// it turned away, a personal one must not — naming them there would answer
/// "does this address have a MyCorr account" for anyone who asked.
const shareErrorMessage = (e: unknown): string => {
  if (!axios.isAxiosError(e)) return SHARE_FALLBACK;
  const data = e.response?.data;
  if (data?.status === "capacity_reached")
    return data.message ?? CAPACITY_FALLBACK;
  if (typeof data?.reason !== "string") return SHARE_FALLBACK;
  const refused: string[] = Array.isArray(data.refused_emails)
    ? data.refused_emails
    : [];
  return refused.length ? `${data.reason} ${refused.join(", ")}` : data.reason;
};

export const EmailSection: React.FC<InputShareFieldProps> = (props) => {
  const [chips, setChips] = useState<EmailChip[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  // Company-org tables restrict entry to the org roster (D19) — the picker
  // makes the door's membership refusal nearly unreachable.
  const shareScope = useShareScope(props.entityId, props.resourceType);

  const defaultPermission =
    props.resourceType === ResourceType.Model
      ? (props.defaultRole ?? UserModelRole.Collaborator)
      : (props.defaultPermission ?? Permission.CanImport);

  const [selectedPermission, setSelectedPermission] = useState<
    UserModelRole | Permission
  >(defaultPermission);

  const permissionOptions: SelectOption[] = useMemo(() => {
    if (props.resourceType === ResourceType.Model) {
      return [UserModelRole.Collaborator, UserModelRole.Viewer].map((role) => ({
        label: UserModelRole[role],
        value: role,
      }));
    }
    return [
      { label: "Can Import", value: Permission.CanImport },
      { label: "Can Share", value: Permission.CanShare },
    ];
  }, [props.resourceType]);

  const handleShare = async () => {
    const validEmails = chips
      .filter((chip) => chip.status === "valid")
      .map((chip) => chip.email);

    if (chips.length === 0) {
      setError("Add at least one email to share.");
      return;
    }

    if (validEmails.length === 0) {
      setError("Resolve the highlighted emails before sharing.");
      return;
    }

    try {
      setIsSharing(true);
      const outcome = await props.shareAction(validEmails, selectedPermission);
      setChips([]);
      const atCapacity = outcome?.atCapacityEmails ?? [];
      if (atCapacity.length > 0) {
        setError(`${CAPACITY_FALLBACK} Not invited: ${atCapacity.join(", ")}`);
        setSuccessMessage(null);
        return;
      }
      // A door that serves what it can (datasets) names what it refused —
      // without this the dialog said "shared successfully" over a partial
      // failure.
      const refused = outcome?.refusedEmails ?? [];
      if (refused.length > 0) {
        setError(`Shared, but not with: ${refused.join(", ")}`);
        setSuccessMessage(null);
        return;
      }
      setError(null);
      const resourceLabel =
        props.resourceType === ResourceType.Model
          ? "Model"
          : props.resourceType === ResourceType.Dataset
            ? "Dataset"
            : "Table";
      setSuccessMessage(`${resourceLabel} shared successfully!`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (shareError) {
      log.error("Failed to share:", shareError);
      setError(shareErrorMessage(shareError));
      setSuccessMessage(null);
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 overflow-visible">
      <div className="flex flex-col gap-1">
        <div className="flex flex-col gap-2">
          <EmailChipInput
            chips={chips}
            onChipsChange={setChips}
            entityId={props.entityId}
            resourceType={props.resourceType}
            error={error}
            onErrorChange={setError}
            directory={shareScope.directory}
            directoryLabel={shareScope.orgName}
            placeholder={
              shareScope.scope === "company"
                ? `Members of ${shareScope.orgName ?? "the organization"}`
                : undefined
            }
          />

          <div className="flex items-center gap-2 justify-end">
            {error ? (
              <span
                className="text-error text-sm mr-auto truncate"
                role="alert"
              >
                {error}
              </span>
            ) : successMessage ? (
              <span className="text-success mr-auto" role="status">
                {successMessage}
              </span>
            ) : null}
            {/* Datasets are flat (no role tier) — the composer offers no role
                picker; the share endpoint ignores any role. */}
            {props.resourceType !== ResourceType.Dataset && (
              <SelectDropdown
                options={permissionOptions}
                value={selectedPermission}
                onChange={(value) =>
                  setSelectedPermission(value as UserModelRole | Permission)
                }
                className="w-30! [&_span]:whitespace-nowrap!"
                menuWidthClass="w-[118px]"
                size="md"
                centerOptions={true}
              />
            )}
            <Button
              onClick={handleShare}
              disabled={
                isSharing ||
                props.shareDisabled ||
                chips.every((chip) => chip.status !== "valid")
              }
              loading={isSharing}
              variant="primary"
              size="sm"
              className="h-8! px-6!"
            >
              Share
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
